import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Field, PasswordInput, PrimaryButton, FormAlert, inputCls } from "@/components/FormBits";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, maskPhone, phoneDigits, UFS } from "@/lib/api";

const EMPTY = { name: "", phone: "", city: "", state: "", email: "", password: "" };

function validate(f) {
  const e = {};
  if (f.name.trim().length < 3) e.name = "Digite seu nome completo";
  const d = phoneDigits(f.phone);
  if (d.length < 10 || d.length > 11) e.phone = "Digite o celular com DDD, ex: (11) 98765-4321";
  if (f.city.trim().length < 2) e.city = "Digite sua cidade";
  if (!f.state) e.state = "Escolha seu estado";
  if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "E-mail inválido";
  if (f.password.length < 6) e.password = "A senha precisa ter pelo menos 6 caracteres";
  return e;
}

export default function Signup() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [optin, setOptin] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/painel" replace />;
  const set = (k) => (ev) => setF({ ...f, [k]: k === "phone" ? maskPhone(ev.target.value) : ev.target.value });

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f);
    setErrors(e);
    setApiError("");
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      await register({ ...f, phone: phoneDigits(f.phone), email: f.email || null, whatsapp_optin: optin });
      navigate("/painel");
    } catch (err) {
      setApiError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testId="signup-page" title="Criar minha conta" subtitle="Leva menos de 2 minutos. Seu celular será o seu login.">
      <form onSubmit={submit} noValidate className="space-y-6" data-testid="signup-form">
        <Field id="signup-name" label="Nome completo" error={errors.name}>
          <input id="signup-name" data-testid="signup-name-input" className={inputCls} value={f.name} onChange={set("name")} autoComplete="name" placeholder="Ex: Maria da Silva" />
        </Field>
        <Field id="signup-phone" label="Celular com WhatsApp" error={errors.phone}>
          <input id="signup-phone" data-testid="signup-phone-input" className={inputCls} value={f.phone} onChange={set("phone")} inputMode="numeric" autoComplete="tel" placeholder="(11) 98765-4321" />
        </Field>
        <div className="grid gap-6 sm:grid-cols-[1fr_190px]">
          <Field id="signup-city" label="Cidade" error={errors.city}>
            <input id="signup-city" data-testid="signup-city-input" className={inputCls} value={f.city} onChange={set("city")} autoComplete="address-level2" placeholder="Sua cidade" />
          </Field>
          <Field id="signup-state" label="Estado" error={errors.state}>
            <select id="signup-state" data-testid="signup-state-select" className={inputCls} value={f.state} onChange={set("state")}>
              <option value="">Escolha</option>
              {UFS.map(([uf, nome]) => <option key={uf} value={uf}>{uf} — {nome}</option>)}
            </select>
          </Field>
        </div>
        <Field id="signup-email" label="E-mail" hint="(opcional)" error={errors.email}>
          <input id="signup-email" data-testid="signup-email-input" type="email" className={inputCls} value={f.email} onChange={set("email")} autoComplete="email" placeholder="seuemail@exemplo.com" />
        </Field>
        <Field id="signup-password" label="Crie uma senha" error={errors.password}>
          <PasswordInput id="signup-password" value={f.password} onChange={set("password")} autoComplete="new-password" />
        </Field>

        <label htmlFor="signup-whatsapp-optin" className="flex cursor-pointer gap-4 rounded-2xl border-2 border-amber-200 bg-amber-50/70 p-4 sm:p-5">
          <input
            id="signup-whatsapp-optin"
            data-testid="signup-whatsapp-optin-checkbox"
            type="checkbox"
            checked={optin}
            onChange={(e) => setOptin(e.target.checked)}
            className="mt-1 h-7 w-7 shrink-0 cursor-pointer accent-emerald-700"
          />
          <span>
            <span className="block text-lg leading-snug text-slate-900">
              Quero receber novidades, oportunidades, benefícios e ofertas selecionadas pelo WhatsApp.
            </span>
            <span className="mt-1 block text-base text-slate-600">Você poderá cancelar quando quiser.</span>
          </span>
        </label>

        <FormAlert testId="signup-error">{apiError}</FormAlert>
        <PrimaryButton type="submit" loading={loading} data-testid="signup-submit-button">Criar conta</PrimaryButton>
        <p className="text-center text-lg text-slate-700">
          Já tem conta?{" "}
          <Link to="/entrar" data-testid="signup-go-login-link" className="font-bold text-emerald-800 underline underline-offset-4">Entrar</Link>
        </p>
      </form>
    </AuthShell>
  );
}
